import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../stores';
import FamlaServices from '../../../services/api/FamlaServices';
import { TextAreaForm, ButtonGroup } from '../../../components';
import Spiner from '../../../components/Spiner';
import { BASE_URL_AUDIO } from '../../../services';
import { Images } from '../../../constants';
import { RenderFormatteContent } from '../../../utils';
import TypingIndicator from '../../../components/Analyse/TypingIndicator';
import { getBrandingColor } from '../../../utils/brandingStyles';

interface Process {
    name: string;
    description: string;
}

interface CampaignSetupMessage {
    sender: 'user' | 'chatbot';
    message: string;
    timestamp: string;
    process_list?: Process[];
}


/* interface ChatbotResponse {
    send: string;
    conversation_complete: number;
    process_list?: SmartObjective[];
} */

interface CampaignChatbotProps {
    campaignId: string | undefined;
    stepName?: string; // Default: "Define_objectives"
}

const CampaignChatbot: React.FC<CampaignChatbotProps> = ({
    campaignId,
    stepName = "Define_objectives"
}) => {
    const { t } = useTranslation();
    const [isAnalysisCompleted, setIsAnalysisCompleted] = useState(false);
    const users = useAppSelector((state) => state.auth);
    const userFullname = users.user?.fullname || users.session?.user?.fullname || 'User';
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);

    // State
    const [messages, setMessages] = useState<CampaignSetupMessage[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [userMessage, setUserMessage] = useState('');
    const [expandedObjectiveIndex, setExpandedObjectiveIndex] = useState<number | null>(null);
    const [displayCreateProcessbutton, setDisplayCreateProcessbutton] = useState(false);
    const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(null);
    const [editedProcesses, setEditedProcesses] = useState<Process[]>([]);
    const [originalProcesses, setOriginalProcesses] = useState<Process[]>([]);
    const [isSubmittingUpdates, setIsSubmittingUpdates] = useState(false);

    // Fetch messages from GET endpoint
    const fetchMessages = async () => {
        if (!campaignId) return;

        try {
            setIsLoading(true);
            const responseAnalysisStatus = await FamlaServices(
                "api_host",
                `/campaign/${campaignId}/progress`,
                "GET",
                "",
                users.access_token
            )
            if (responseAnalysisStatus.status === 200 && responseAnalysisStatus.body.data) {
                setIsAnalysisCompleted(responseAnalysisStatus.body.data.objectives_defined);
            }

            const response = await FamlaServices(
                "api_host",
                `/campaign/${campaignId}/${stepName}/messages`,
                "GET",
                "",
                users.access_token
            );

            if (response.status === 200 && response.body.data) {
                const data = response.body.data;
                // Set messages from the response
                if (data.status === "waiting_for_validation") {
                    setDisplayCreateProcessbutton(true);
                }
                
                if (data.messages && Array.isArray(data.messages)) {
                    const new_messages = data.messages.map((msg: any) => ({
                        sender: msg.sender,
                        message: msg.message,
                        timestamp: msg.timestamp,
                        process_list: msg.process_list
                    }))
                    console.log("new_messages", new_messages);
                    setMessages(new_messages);
                } else {
                    setMessages([]);
                }
            } else {
                setMessages([]);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
            setMessages([]);
        } finally {
            setIsLoading(false);
        }
    };

    const checkAnalysisCompleted = async () => {
        try {
            
        } catch (error) {
            console.error('Error checking analysis completed:', error);
        }
    };

    // Fetch initial messages on mount and when campaignId/stepName changes
    useEffect(() => {
        if (campaignId) {
            fetchMessages();
            checkAnalysisCompleted();
        } else {
            setIsLoading(false);
        }

        return () => {
            setDisplayCreateProcessbutton(false); //reset the display create process button
        };
    }, [campaignId, stepName]);

    // Scroll to bottom when messages change
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    // Cleanup audio on unmount
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            if ('speechSynthesis' in window) {
                window.speechSynthesis.cancel();
            }
            setIsPlaying(false);
            setPlayingMessageId(null);
        };
    }, []);

    const handleSendMessage = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setDisplayCreateProcessbutton(false);
        
        if (!campaignId || !userMessage.trim() || isSending) return;

        const messageToSend = userMessage.trim();
        setUserMessage('');
        setIsSending(true);

        // Optimistically add user message
        const newUserMessage: CampaignSetupMessage = {
            sender: 'user',
            message: messageToSend,
            timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, newUserMessage]);

        try {
            // Retrieve messages from POST endpoint
            const response = await FamlaServices(
                "api_host",
                `/campaign/${campaignId}/${stepName}/messages`,
                "POST",
                { message: messageToSend },
                users.access_token
            );

            if (response.status === 201 && response.body.data) {
                const chatbotResponse: CampaignSetupMessage = response.body.data.chatbot_response;
                const stepStatus = response.body.data.step_status;
                if (stepStatus === "waiting_for_validation") {
                    setDisplayCreateProcessbutton(true);
                }else{
                    setDisplayCreateProcessbutton(false);
                }
                // Add chatbot response message from POST response
                const newChatbotMessage: CampaignSetupMessage = {
                    sender: 'chatbot',
                    message: chatbotResponse.message,
                    timestamp: new Date().toISOString(),
                    process_list: chatbotResponse.process_list
                };
                setMessages(prev => [...prev, newChatbotMessage]);
            } else {
                // Remove optimistic message on error
                setMessages(prev => prev.slice(0, -1));
                console.error('Failed to send message:', response.body);
            }
        } catch (error) {
            console.error('Error sending message:', error);
            // Remove optimistic message on error
            setMessages(prev => prev.slice(0, -1));
        } finally {
            setIsSending(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setUserMessage(e.target.value);
    };

    const handleUpdateAnswer = (answer: string) => {
        setUserMessage(answer);
    };

    const toggleObjective = (index: number) => {
        setExpandedObjectiveIndex(expandedObjectiveIndex === index ? null : index);
    };

    const CreateProcessAnalysys = async () => {
        try {
            const response = await FamlaServices(
                "api_host",
                `/campaign/${campaignId}/create-processes`,
                "POST",
                {},
                users.access_token
            );

            if (response.status === 201 && response.body.data) {
                // Success - reload the page
                window.location.reload();
            } else {
                // Error - show alert with error message
                const errorMessage = response.body?.description || response.body?.response_type || 'Failed to create processes';
                alert(errorMessage);
            }
        } catch (error: any) {
            // Exception - show alert with error
            const errorMessage = error?.response?.body?.description || error?.message || 'An error occurred while creating processes';
            alert(errorMessage);
            console.error('Error creating processes:', error);
        }
    };

    const handleEditProcesses = (messageIndex: number) => {
        const message = messages[messageIndex];
        if (message.process_list) { //the message should contain a list of processes so we can update it
            setEditingMessageIndex(messageIndex);
            setOriginalProcesses([...message.process_list]);
            setEditedProcesses([...message.process_list]);
        }
    };

    const handleCancelEdit = () => {
        setEditingMessageIndex(null);
        setEditedProcesses([]);
        setOriginalProcesses([]);
    };

    const handleProcessChange = (processIndex: number, field: 'name' | 'description', value: string) => {
        setEditedProcesses(prev => {
            const updated = [...prev];
            updated[processIndex] = {
                ...updated[processIndex],
                [field]: value
            };
            return updated;
        });
    };

    const handleSubmitProcessUpdates = async () => {
        if (!campaignId || editingMessageIndex === null) return;

        setIsSubmittingUpdates(true);
        // Optimistically add user message
        const newUserMessage: CampaignSetupMessage = {
            sender: 'user',
            message: t('campaigns.updateProcesses'),
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, newUserMessage]);
        try {
            // Prepare request body with original and updated processes
            const requestBody = {
                original_processes: originalProcesses,
                updated_processes: editedProcesses
            };

            const response = await FamlaServices(
                "api_host",
                `/campaign/${campaignId}/${stepName}/update-process-list`,
                "PUT",
                requestBody,
                users.access_token
            );

            if (response.status === 200 && response.body.data) {
                // Reset edit mode
                handleCancelEdit();
                
                // Add chatbot response message from POST response
                const chatbotResponse: CampaignSetupMessage = response.body.data.chatbot_response;
                const newChatbotMessage: CampaignSetupMessage = {
                    sender: 'chatbot',
                    message: chatbotResponse.message,
                    timestamp: new Date().toISOString(),
                    process_list: chatbotResponse.process_list
                };
                setMessages(prev => [...prev, newChatbotMessage]);
            } else {
                // Error - show alert with error message
                const errorMessage = response.body?.description || response.body?.response_type || 'Failed to update processes';
                alert(errorMessage);
            }
        } catch (error: any) {
            // Exception - show alert with error
            const errorMessage = error?.response?.body?.description || error?.message || 'An error occurred while updating processes';
            alert(errorMessage);
            console.error('Error updating processes:', error);
        } finally {
            setIsSubmittingUpdates(false);
        }
    };

    const formatTimestamp = (timestamp: string) => {
        try {
            const date = new Date(timestamp);
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '';
        }
    };

    const handleSynthesize = async (messageId: string, messageText: string) => {
        try {
            if (audioRef.current) {
                // Si l'audio est déjà en cours de lecture et que l'utilisateur veut l'arrêter
                if (isPlaying && !audioRef.current.paused && playingMessageId === messageId) {
                    audioRef.current.pause();
                    setIsPlaying(false);
                    setPlayingMessageId(null);
                    return;
                }

                // Si l'audio est en pause, le reprendre
                if (audioRef.current.paused && playingMessageId === messageId) {
                    audioRef.current.play().catch(() => {
                        console.error("Failed to resume audio");
                    });
                    setIsPlaying(true);
                    return;
                }
            }

            setIsPlaying(true);
            setPlayingMessageId(messageId);

            // TODO: Replace with actual campaign message audio endpoint when available
            // For now, we'll use a text-to-speech approach or skip audio for campaign messages
            // const response = await FamlaServices("api_host", `/campaign/${campaignId}/audio/${messageId}`, "GET", "", users.access_token);
            
            // Temporary: Use browser's speech synthesis as fallback
            if ('speechSynthesis' in window) {
                // Stop any ongoing speech
                window.speechSynthesis.cancel();
                
                const utterance = new SpeechSynthesisUtterance(messageText);
                utterance.lang = 'fr-FR'; // Adjust based on user language
                utterance.onend = () => {
                    setIsPlaying(false);
                    setPlayingMessageId(null);
                };
                utterance.onerror = () => {
                    setIsPlaying(false);
                    setPlayingMessageId(null);
                };
                window.speechSynthesis.speak(utterance);
            } else {
                setIsPlaying(false);
                setPlayingMessageId(null);
            }
        } catch (error) {
            console.error('Error synthesizing audio:', error);
            setIsPlaying(false);
            setPlayingMessageId(null);
        }
    };

    if (!campaignId) {
        return (
            <div className="d-flex align-items-center justify-content-center h-100">
                <div className="text-center text-light">
                    <i className="bi bi-chat-dots fs-1 mb-3"></i>
                    <h4>{t('campaigns.selectCampaign')}</h4>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex flex-column h-100">
            {/* Chat Messages Container */}
            <div
                ref={chatContainerRef}
                className="flex-grow-1 overflow-auto px-3 py-4"
                style={{ maxHeight: 'calc(100vh - 200px)' }}
            >
                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <Spiner className="spinner-border-sm" />
                    </div>
                ) : (
                    <>
                        {messages.length === 0 ? (
                            <div className="d-flex align-items-center justify-content-center h-100">
                                <div className="text-center text-light">
                                    <i className="bi bi-chat-dots fs-1 mb-3"></i>
                                    <p className="text-secondary">
                                        {t('campaigns.noMessages')}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex flex-column gap-3">
                                {messages.map((message, index) => {
                                    const messageId = `msg-${index}-${message.timestamp}`;
                                    const isLastMessage = index === messages.length - 1;
                                    
                                    return (
                                        <div key={index} className="d-flex flex-column gap-2">
                                            {message.sender === 'user' ? (
                                                // User message - styled like ContentQuestion
                                                <div className="mt-4 d-flex align-items-center content-container">
                                                    <div
                                                        className="d-flex justify-content-center align-items-center text-light border-0 rounded-circle edit-btn me-3"
                                                        style={{ width: '40px', height: '40px' }}
                                                    ></div>
                                                    <div className="w-100 bg-dark p-3 rounded-4 card me-3">
                                                        <div className="d-flex align-items-center">
                                                            <i className="bi bi-person fs-5 text-light"></i>
                                                            <span className="ps-1 text-light">{userFullname}</span>
                                                        </div>
                                                        <div className="mt-2">
                                                            <p className="fs-6 text-light fw-lighter custom-control m-0">
                                                                {message.message && RenderFormatteContent(message.message)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                // Chatbot message - styled like Question
                                                <div className="mt-4">
                                                    <img src={Images.logov1} alt="Logo" className='imgFluid' />
                                                    <p className="fs-6 ps-4 text-light fw-lighter mt-1 costom-control">
                                                        <span className='text-pre-wrap'>{message.message}</span>
                                                    </p>
                                                    
                                                    {/* Speaker button for chatbot messages */}
                                                    {isLastMessage && (
                                                        <ButtonGroup
                                                            buttons={[
                                                                {
                                                                    text: '',
                                                                    onClick: () => handleSynthesize(messageId, message.message),
                                                                    icon: isPlaying && playingMessageId === messageId ? (
                                                                        <i className="fa-solid fa-volume-xmark animate-pulse-bootstrap text-white"></i>
                                                                    ) : (
                                                                        <i className="fa-solid fa-volume-high"></i>
                                                                    ),
                                                                    isSelected: false
                                                                },
                                                            ]}
                                                            className="ps-4 justify-content-end mt-3"
                                                            ariaLabel="Speaker button"
                                                        />
                                                    )}
                                                </div>
                                            )}

                                            {/* Process List Expandable Panels */}
                                            {message.process_list && message.process_list.length > 0 && (
                                                <div className={`${message.sender === 'user' ? 'd-flex justify-content-end' : ''}`}>
                                                    <div style={{ maxWidth: '75%', width: '100%' }}>
                                                        <div className="d-flex flex-column gap-2">
                                                            {(editingMessageIndex === index ? editedProcesses : message.process_list).map((process, processIndex) => (
                                                                <div
                                                                    key={processIndex}
                                                                    className="card bg-dark border-secondary"
                                                                >
                                                                    <div
                                                                        className="card-header bg-transparent border-secondary d-flex justify-content-between align-items-center"
                                                                        style={{ cursor: editingMessageIndex === index ? 'default' : 'pointer' }}
                                                                        onClick={() => editingMessageIndex !== index && toggleObjective(index * 1000 + processIndex)}
                                                                    >
                                                                        {editingMessageIndex === index ? (
                                                                            <input
                                                                                type="text"
                                                                                className="form-control bg-dark text-light border-secondary"
                                                                                value={process.name}
                                                                                onChange={(e) => handleProcessChange(processIndex, 'name', e.target.value)}
                                                                                disabled={true}
                                                                                style={{ maxWidth: '100%' }}
                                                                            />
                                                                        ) : (
                                                                            <h6 className="mb-0 text-light">
                                                                                <i className={`bi bi-chevron-${expandedObjectiveIndex === index * 1000 + processIndex ? 'down' : 'right'} me-2`}></i>
                                                                                {process.name}
                                                                            </h6>
                                                                        )}
                                                                    </div>
                                                                    {(expandedObjectiveIndex === index * 1000 + processIndex || editingMessageIndex === index) && (
                                                                        <div className="card-body text-light">
                                                                            {editingMessageIndex === index ? (
                                                                                <textarea
                                                                                    className="form-control bg-dark text-light border-secondary"
                                                                                    value={process.description}
                                                                                    onChange={(e) => handleProcessChange(processIndex, 'description', e.target.value)}
                                                                                    rows={3}
                                                                                    style={{ resize: 'vertical' }}
                                                                                />
                                                                            ) : (
                                                                                <p className="mb-0">{process.description}</p>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                            
                                                            {/* Edit/Submit/Cancel Buttons */}
                                                            {editingMessageIndex === index ? (
                                                                <div className="d-flex gap-2 justify-content-end mt-2">
                                                                    <button
                                                                        onClick={handleCancelEdit}
                                                                        className="btn btn-secondary"
                                                                        disabled={isSubmittingUpdates}
                                                                    >
                                                                        {t('words.cancel')}
                                                                    </button>
                                                                    <button
                                                                        onClick={handleSubmitProcessUpdates}
                                                                        className="btn btn-primary"
                                                                        disabled={isSubmittingUpdates}
                                                                        style={{ backgroundColor: getBrandingColor(), borderColor: getBrandingColor() }}
                                                                    >
                                                                        {isSubmittingUpdates ? (
                                                                            <>
                                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                                Submitting...
                                                                            </>
                                                                        ) : (
                                                                            'Submit Updates'
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            ) : index === messages.length - 1 && !isAnalysisCompleted && ( //only display the editprocess button for the last message and if the analysis is not completed
                                                                <div className="d-flex justify-content-end mt-2">
                                                                    <button
                                                                        onClick={() => handleEditProcesses(index)} //the index of the message that we want to edit
                                                                        className="btn btn-sm"
                                                                        style={{ backgroundColor: getBrandingColor(), borderColor: getBrandingColor(), color: 'white' }}
                                                                    >
                                                                        <i className="bi bi-pencil me-2"></i>
                                                                        {t('campaigns.editProcesses')}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                
                                {/* Typing Indicator */}
                                {isSending && (
                                    <div className="mt-4">
                                        <img src={Images.logov1} alt="Logo" className='imgFluid' />
                                        <div className="ps-4 mt-1">
                                            <TypingIndicator />
                                        </div>
                                    </div>
                                )}
                                
                                {/* Create Processes Analysis Button */}
                                                {displayCreateProcessbutton && (
                                    <div className="d-flex justify-content-center mt-4">
                                        <button
                                            onClick={CreateProcessAnalysys}
                                            className="btn btn-lg"
                                            style={{ backgroundColor: getBrandingColor(), borderColor: getBrandingColor() }}
                                        >
                                            {t('campaigns.createProcessesAnalysis')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Input Area */}
            {isAnalysisCompleted ?(
                <div className="border-top border-secondary px-3 py-3">
                    <div className={`w-100 message-box rounded-4`}>
                        <p className="fs-6 ps-4 text-light fw-lighter mt-1 costom-control text-center w-full">
                            {t('campaigns.analysisCompleted')}
                        </p>
                    </div>
                </div>
            )
            : (
            <div className="border-top border-secondary px-3 py-3">
                <div className={`w-100 message-box ${isFocused && 'border-theme'} rounded-4`}>
                    <TextAreaForm
                        name="campaignMessage"
                        sendButton={userMessage.trim().length > 0 && !isSending}
                        label=""
                        rows={1}
                        classNameArea="w-100 bg-transparent text-white text-bg-dark py-2 px-3 mb-0 rounded-4 border-0"
                        classNameContainer="w-100"
                        placeholder={t('campaigns.typeMessage')}
                        onChange={handleInputChange}
                        value={userMessage}
                        interviewId=""
                        sendAnswer={handleSendMessage}
                        spinner={isSending}
                        maxRows={5}
                        minRows={1}
                        button={true}
                        readOnly={isSending}
                        updateAnswer={handleUpdateAnswer}
                        handleFocus={() => setIsFocused(true)}
                        handleBlur={() => setIsFocused(false)}
                    />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CampaignChatbot;

